'use client';
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { ArrowRight, Zap } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface ActiveIssue {
  name: string;
  artworkDeadline: string;
}

const formatDisplayDate = (input: string): string => {
  if (!input) return '';
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return input;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export default function CtaBanner() {
  const [activeIssue, setActiveIssue] = useState<ActiveIssue | null>(null);

  useEffect(() => {
    const supabase = createClient();
    const fetchActiveIssue = async () => {
      const { data } = await supabase
        .from('issues')
        .select('issue_number, name, artwork_deadline')
        .eq('status', 'booking')
        .order('issue_number', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (data) {
        setActiveIssue({
          name: data.name || `Issue #${data.issue_number}`,
          artworkDeadline: formatDisplayDate(data.artwork_deadline),
        });
      }
    };
    void fetchActiveIssue();
  }, []);

  return (
    <section className="py-20">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        <div className="relative glass-card rounded-3xl p-12 text-center overflow-hidden">
          {/* Background glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-48 bg-brand-600/15 blur-3xl rounded-full" />
          </div>

          <div className="relative z-10">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-primary mb-6 glow-primary mx-auto">
              <Zap size={24} className="text-white" />
            </div>
            <h2 className="font-display text-4xl sm:text-5xl font-800 text-white mb-4">
              Ready to Fill Your <span className="text-gradient">Next Issue?</span>
            </h2>
            <p className="text-slate-400 max-w-lg mx-auto mb-8">
              {activeIssue ? (
                <>
                  Artwork deadline for <strong className="text-white">{activeIssue.name}</strong> is{' '}
                  <strong className="text-white">{activeIssue.artworkDeadline}</strong>. Reserve your spot before inventory sells out.
                </>
              ) : (
                <>Reserve your spot in our next issue before inventory sells out.</>
              )}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/order-wizard" className="flex items-center gap-2 px-8 py-4 text-base font-semibold btn-primary">
                Book an Ad Now
                <ArrowRight size={18} />
              </Link>
              <Link href="/regions-pricing" className="flex items-center gap-2 px-8 py-4 text-base font-medium btn-outline">
                Check Availability
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}